'use client';
import { Zap, ShieldCheck, Tag, Headphones } from 'lucide-react';
import { LegalShell, LegalSection, EntityCard, useLang, type Lang } from '@/components/LegalShell';

const C: Record<Lang, { title: string; sub: string; intro: string; feats: { icon: 'zap' | 'shield' | 'tag' | 'support'; t: string; d: string }[]; entityNote: string }> = {
  uk: {
    title: 'Про нас', sub: 'GamesSales — магазин цифрових ігор і товарів для Xbox / Microsoft.',
    intro: 'Ми допомагаємо гравцям купувати ігри, доповнення, gift-коди й ігрові акаунти за вигідними цінами з різних регіонів. Швидка доставка, чесні ціни й підтримка на кожному кроці.',
    entityNote: 'Юридична інформація',
    feats: [
      { icon: 'zap', t: 'Швидка доставка', d: 'Ключ на email протягом 1–24 годин після оплати.' },
      { icon: 'shield', t: 'Безпечно', d: 'Ліцензовані платіжні оператори, дані карток не зберігаємо.' },
      { icon: 'tag', t: 'Вигідні ціни', d: 'Регіональні ціни й регулярні знижки.' },
      { icon: 'support', t: 'Підтримка', d: 'Допоможемо з активацією й будь-яким питанням.' },
    ],
  },
  ru: {
    title: 'О нас', sub: 'GamesSales — магазин цифровых игр и товаров для Xbox / Microsoft.',
    intro: 'Мы помогаем игрокам покупать игры, дополнения, gift-коды и игровые аккаунты по выгодным ценам из разных регионов. Быстрая доставка, честные цены и поддержка на каждом шаге.',
    entityNote: 'Юридическая информация',
    feats: [
      { icon: 'zap', t: 'Быстрая доставка', d: 'Ключ на email в течение 1–24 часов после оплаты.' },
      { icon: 'shield', t: 'Безопасно', d: 'Лицензированные платёжные операторы, данные карт не храним.' },
      { icon: 'tag', t: 'Выгодные цены', d: 'Региональные цены и регулярные скидки.' },
      { icon: 'support', t: 'Поддержка', d: 'Поможем с активацией и любым вопросом.' },
    ],
  },
  en: {
    title: 'About us', sub: 'GamesSales — a store of digital games and goods for Xbox / Microsoft.',
    intro: 'We help players buy games, DLC, gift codes and game accounts at great prices from various regions. Fast delivery, fair prices and support at every step.',
    entityNote: 'Legal information',
    feats: [
      { icon: 'zap', t: 'Fast delivery', d: 'Key to your email within 1–24 hours after payment.' },
      { icon: 'shield', t: 'Secure', d: 'Licensed payment operators; we don’t store card data.' },
      { icon: 'tag', t: 'Great prices', d: 'Regional pricing and regular discounts.' },
      { icon: 'support', t: 'Support', d: 'We help with activation and any question.' },
    ],
  },
};

const ICONS = { zap: Zap, shield: ShieldCheck, tag: Tag, support: Headphones };

export default function AboutPage() {
  const lang = useLang();
  const c = C[lang];
  return (
    <LegalShell title={c.title} subtitle={c.sub}>
      <LegalSection><p style={{ margin: 0 }}>{c.intro}</p></LegalSection>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12, marginBottom: 28 }}>
        {c.feats.map(f => {
          const Ico = ICONS[f.icon];
          return (
            <div key={f.t} style={{ padding: '16px 18px', borderRadius: 14, background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: 'color-mix(in srgb, var(--gs-green) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--gs-green) 28%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}><Ico size={19} color="var(--gs-green)" /></div>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--fg)', marginBottom: 4 }}>{f.t}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-3)', lineHeight: 1.6 }}>{f.d}</div>
            </div>
          );
        })}
      </div>
      <LegalSection heading={c.entityNote}><EntityCard /></LegalSection>
    </LegalShell>
  );
}
